import Decimal from "decimal.js-light"
import { type FC, useReducer, useState } from "react"
import styled from "styled-components"

import dayjs from "@forento/core/utilities/dayjs"
import { parseIntNumber, parseNumber } from "@forento/core/utilities/number"
import { exhaustiveGuard } from "@forento/core/utilities/switch"

import Button, { SubmitButton } from "~/components/Button"
import Checkbox from "~/components/Checkbox"
import Dropdown from "~/components/Dropdown"
import InputField from "~/components/InputField"
import { DefaultModal, ModalButtons, ModalInputs, ModalTitle } from "~/components/Modal"
import { useAlert } from "~/contexts/AlertContext"
import { useUser } from "~/contexts/UserContext"
import { query } from "~/utilities/trpc"

const discountTypes = ["percent", "amount"] as const
const durations = [
	{ id: "once", label: "Once" },
	{ id: "repeating", label: "Repeating" },
	{ id: "forever", label: "Forever" },
] as const

type DiscountType = (typeof discountTypes)[number]
type Duration = (typeof durations)[number]

type Props = { isOpen: boolean; onClose(): void; onSubmit(): Promise<void> }
const CreateCodeModal: FC<Props> = ({ isOpen, onClose, onSubmit }) => {
	const platform = useUser().user!.platform!
	const alert = useAlert()

	const isStripe = platform.paymentGateway?.type === "stripe"

	const discountTypeNames: Record<DiscountType, string> = { amount: platform.currency, percent: "%" }

	const [name, setName] = useState("")
	const [code, setCode] = useReducer((_: unknown, value: string) => value.trim().toUpperCase(), "")
	const [discount, setDiscount] = useState("10")
	const [discountType, setDiscountType] = useState<DiscountType>("percent")
	const [duration, setDuration] = useState<Duration["id"]>("once")
	const [durationInMonths, setDurationInMonths] = useState("1")
	const [isMaxRedemptionsEnabled, setMaxRedemptionsEnabled] = useState(false)
	const [maxRedemptions, setMaxRedemptions] = useState("0")
	const [isRedemptionDateLimited, setRedemptionDateLimited] = useState(false)
	const [redemptionDate, setRedemptionDate] = useState(dayjs().add(1, "month").format("YYYY-MM-DDT00:00"))

	async function handleSuccess({ status }: { status: "success" | "invalid-code" | "code-taken" }) {
		switch (status) {
			case "invalid-code": {
				await alert.show("Invalid code", "That code is invalid. It can only contain letters and numbers.")
				break
			}
			case "code-taken": {
				await alert.show("Code already taken", "That code is already in use for another discount code.")
				break
			}
			case "success": {
				setName("")
				setCode("")
				setDiscount("10")
				setDiscountType("percent")
				setDuration("once")
				setDurationInMonths("1")
				setMaxRedemptionsEnabled(false)
				setMaxRedemptions("0")
				setRedemptionDateLimited(false)
				setRedemptionDate(dayjs().add(1, "month").format("YYYY-MM-DDT00:00"))
				await onSubmit()
				break
			}
			default: {
				exhaustiveGuard(status)
			}
		}
	}

	async function handleError(error: unknown) {
		console.error(error)
		await alert.show("Error", "An error occurred while creating the discount code. Please try again.")
	}

	const createStripe = query.payment.discountCode.createStripe.useMutation({
		onSuccess: handleSuccess,
		onError: handleError,
	})
	const createPaypal = query.payment.discountCode.createPaypal.useMutation({
		onSuccess: handleSuccess,
		onError: handleError,
	})

	const handleSubmit = async () => {
		const lastRedemptionDate = isRedemptionDateLimited ? new Date(redemptionDate) : null
		const discountValue = parseIntNumber(discount)

		// Name validation
		if (name.trim().length === 0) {
			await alert.show("Name too short", "The name can't be empty.")
			return
		}
		if (name.trim().length > 40) {
			await alert.show("Name too long", "The name can't be longer than 40 characters.")
			return
		}

		// Code validation
		if (code.trim().length === 0) {
			await alert.show("Code too short", "The code can't be empty.")
			return
		}
		if (code.length > 20) {
			await alert.show("Code too long", "The code can't be longer than 20 characters.")
			return
		}
		if (!/^[A-Z0-9]+$/.test(code)) {
			await alert.show("Invalid code", "The code can only contain uppercase letters and numbers.")
			return
		}

		// Discount validation
		if (discountValue === null) {
			await alert.show("Invalid discount", "The discount must be a whole number.")
			return
		}
		if (discountValue <= 0) {
			await alert.show("Invalid discount", "The discount must be greater than 0.")
			return
		}

		// Max redemptions validation
		if (isMaxRedemptionsEnabled) {
			const maxRedemptionsValue = parseNumber(maxRedemptions)
			if (maxRedemptionsValue === null) {
				await alert.show("Invalid max redemptions", "The max redemptions must be a number.")
				return
			}
			if (maxRedemptionsValue <= 0) {
				await alert.show("Invalid max redemptions", "The max redemptions must be greater than 0.")
				return
			}
		}

		// Date validation
		if (lastRedemptionDate && lastRedemptionDate < new Date()) {
			await alert.show("Invalid date", "The last redemption date can't be in the past.")
			return
		}

		if (isStripe) {
			// Stripe-specific validation
			if (discountType === "percent") {
				if (discountValue > 100) {
					await alert.show("Invalid discount", "The discount must be between 1 and 100 percent.")
					return
				}
			}

			const durationValue = (() => {
				if (duration !== "repeating") {
					return { type: duration }
				}
				const value = parseNumber(durationInMonths)
				if (value === null) {
					alert.show("Invalid duration", "The duration must be a number.")
					return null
				}
				if (value <= 0) {
					alert.show("Invalid duration", "The duration must be greater than 0 months.")
					return null
				}
				if (value > 36) {
					alert.show("Invalid duration", "The duration can't be longer than 36 months.")
					return null
				}
				return { type: "repeating" as const, months: value }
			})()
			if (durationValue === null) return

			if (durationValue.type === "forever" && discountType === "amount") {
				await alert.show("Invalid discount", "Fixed-amount discount codes can't be set to last forever.")
				return
			}

			createStripe.mutate({
				name,
				code,
				discount: { type: discountType, value: discountValue },
				maxRedemptions: isMaxRedemptionsEnabled ? parseNumber(maxRedemptions) : null,
				lastRedemptionDate,
				duration: durationValue,
			})
		} else {
			// PayPal-specific validation
			if (discountType === "percent") {
				if (discountValue >= 100) {
					await alert.show("Invalid discount", "The discount must be between 1 and 99 percent.")
					return
				}
			}

			createPaypal.mutate({
				name,
				code,
				discount:
					discountType === "percent"
						? { type: "percent", value: new Decimal(discountValue).div(100).toDecimalPlaces(4).toNumber() }
						: { type: discountType, value: discountValue },
				maxRedemptions: isMaxRedemptionsEnabled ? parseNumber(maxRedemptions) : null,
				lastRedemptionDate,
			})
		}
	}

	return (
		<DefaultModal onClose={onClose} isOpen={isOpen} onSubmit={handleSubmit}>
			<ModalTitle>Create discount code</ModalTitle>
			<ModalInputs>
				<InputField label="Name" value={name} onChange={setName} />
				<InputField label="Code" value={code} onChange={setCode} />
				<DiscountContainer>
					<DiscountValue label="Discount" value={discount} onChange={setDiscount} />
					<DiscountUnit
						items={discountTypes.map(type => ({ id: type, title: discountTypeNames[type] }))}
						selectedItemId={discountType}
						onChange={type => setDiscountType(discountTypes.find(x => x === type) ?? "percent")}
					/>
				</DiscountContainer>
				<Group>
					<Checkbox
						label="Limit number of redemptions"
						isChecked={isMaxRedemptionsEnabled}
						onChange={setMaxRedemptionsEnabled}
					/>
					<InputField
						label="Max redemptions"
						value={isMaxRedemptionsEnabled ? maxRedemptions : ""}
						onChange={setMaxRedemptions}
						disabled={!isMaxRedemptionsEnabled}
					/>
				</Group>
				{isStripe && (
					<Group>
						<Dropdown
							items={durations.map(duration => ({ id: duration.id, title: duration.label }))}
							selectedItemId={duration}
							onChange={id => setDuration(durations.find(x => x.id === id)?.id ?? "once")}
						/>
						{duration === "repeating" && (
							<InputField
								label="Duration in months"
								value={durationInMonths}
								onChange={setDurationInMonths}
							/>
						)}
					</Group>
				)}
				<Group>
					<Checkbox
						label="Limit redemption time"
						isChecked={isRedemptionDateLimited}
						onChange={setRedemptionDateLimited}
					/>
					<InputField
						inputType="datetime-local"
						label="Last redemption date"
						value={isRedemptionDateLimited ? redemptionDate : ""}
						onChange={setRedemptionDate}
						disabled={!isRedemptionDateLimited}
					/>
				</Group>
			</ModalInputs>
			<ModalButtons>
				<Button
					variant="secondary"
					isDisabled={createStripe.isPending || createPaypal.isPending}
					onClick={onClose}
				>
					Cancel
				</Button>
				<SubmitButton variant="primary" isLoading={createStripe.isPending || createPaypal.isPending}>
					Create
				</SubmitButton>
			</ModalButtons>
		</DefaultModal>
	)
}

const DiscountContainer = styled.div`
	display: flex;
	gap: 8px;
	align-items: center;
`

const DiscountValue = styled(InputField)`
	flex: 1 1 auto;
`

const DiscountUnit = styled(Dropdown)`
	flex: 0 0 100px;
	margin-top: 26px;
`

const Group = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
`

export default CreateCodeModal
